import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

type FetchNativeCollabFn = (shouldCheckCache: boolean) => Promise<any>;

/**
 * Hook that wraps the fetchNativeCollab function from NativeCollabComponent
 * and provides optimized refetch capabilities via preloading.
 *
 * This hook enables a performance optimization pattern where native collab data
 * can be preloaded early (e.g., when hovering over a publish button), and then
 * the subsequent refetch can use the cached result for faster execution.
 *
 * @param fetchNativeCollab - The fetchNativeCollab function from NativeCollabComponent
 * @returns An object containing:
 *   - refetchNativeCollab: Callback to refetch data, uses cache if preloaded
 *   - preloadNativeCollabRefetch: Callback to preload data into cache
 */
export const useRefetchNativeCollab = (fetchNativeCollab: FetchNativeCollabFn) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasPreloadedResult = useRef<boolean>(false);

	// Fetch native collab data into cache
	const preloadNativeCollabRefetch = useCallback(async () => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'preloaded',
				actionSubject: 'refetchNativeCollab',
				source: 'useRefetchNativeCollab',
			},
		}).fire();

		try {
			const data = await fetchNativeCollab(false);
			hasPreloadedResult.current = true;
			return data;
		} catch (error) {
			hasPreloadedResult.current = false;
			throw error;
		}
	}, [createAnalyticsEvent, fetchNativeCollab]);

	const refetchNativeCollab = useCallback(
		async (shouldCheckCache?: boolean) => {
			// Use cache if preloaded, otherwise use the provided value
			const checkCache = hasPreloadedResult.current || !!shouldCheckCache;
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'fetched',
					actionSubject: 'refetchNativeCollab',
					source: 'useRefetchNativeCollab',
					attributes: {
						checkCache,
						hasPreloadedResult: hasPreloadedResult.current,
					},
				},
			}).fire();

			try {
				return await fetchNativeCollab(checkCache);
			} catch (error) {
				hasPreloadedResult.current = false;
				throw error;
			}
		},
		[createAnalyticsEvent, fetchNativeCollab],
	);

	return { refetchNativeCollab, preloadNativeCollabRefetch };
};
